import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, AlertCircle, PackageCheck } from "lucide-react";

import { trackEnquiry, normalizeAckNumber } from "../../config/track-api";
import TrackTimeline from "./TrackTimeline";
import "./track.css";

const GENERIC_ERROR =
  "Reference number not found. Please check the number and try again.";

const TrackEnquiry = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [value, setValue]       = useState("");
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState("");
  const [isLoading, setLoading] = useState(false);

  const resultRef = useRef(null);
  const inputRef  = useRef(null);

  const lookup = async (rawNumber) => {
    const acknowledgementNumber = normalizeAckNumber(rawNumber);

    if (!acknowledgementNumber) {
      setError("Please enter your reference number.");
      setResult(null);
      inputRef.current?.focus();
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      const data = await trackEnquiry(acknowledgementNumber);
      setResult(data);
      // Shareable/bookmarkable, and survives a refresh.
      setSearchParams({ ref: acknowledgementNumber }, { replace: true });
    } catch (err) {
      // Every failure reads the same to the user. Distinguishing "malformed"
      // from "not found" would let someone probe which numbers exist.
      const status = err?.response?.status;
      setError(
        status === 429
          ? "Too many attempts. Please wait a minute and try again."
          : GENERIC_ERROR
      );
      setSearchParams({}, { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    lookup(value);
  };

  // Deep link support: /track-your-book?ref=PP-2026-7K4M92 looks up on load,
  // so the number can be linked straight from an acknowledgement email.
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      setValue(normalizeAckNumber(ref));
      lookup(ref);
    }
    // Intentionally runs once — later changes come from the form itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bring the result into view on smaller screens where it lands below the fold.
  useEffect(() => {
    if (result && window.innerWidth < 992) {
      resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  return (
    <section className="tk-section">
      <div className="container">

        {/* ── Search ─────────────────────────────────────────────── */}
        <div className="tk-search-card">
          <p className="tk-search-sub">
            Enter the reference number you received when you submitted your
            quote request.
          </p>

          <form className="tk-form" onSubmit={handleSubmit} noValidate>
            <label className="sr-only" htmlFor="tk-input">
              Reference number
            </label>

            <div className="tk-input-row">
              <input
                id="tk-input"
                ref={inputRef}
                type="text"
                className={`tk-input ${error ? "tk-input--error" : ""}`}
                placeholder="PP-2026-7K4M92"
                value={value}
                onChange={(e) => setValue(e.target.value.toUpperCase())}
                autoComplete="off"
                spellCheck="false"
                /* Shows "Search" instead of a newline key on mobile keyboards. */
                enterKeyHint="search"
                maxLength={30}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? "tk-error" : undefined}
                disabled={isLoading}
              />

              <button type="submit" className="tk-btn" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="tk-spinner" aria-hidden="true" />
                    Checking…
                  </>
                ) : (
                  <>
                    <Search size={16} strokeWidth={2.2} />
                    Track Status
                  </>
                )}
              </button>
            </div>

            {error && (
              <p className="tk-error" id="tk-error" role="alert">
                <AlertCircle size={15} strokeWidth={2.2} />
                <span>{error}</span>
              </p>
            )}
          </form>
        </div>

        {/* ── Result ─────────────────────────────────────────────── */}
        {result && (
          <div className="tk-result" ref={resultRef}>
            <div className="tk-result-head">
              {result.bookTitle && (
                <div className="tk-result-meta tk-result-meta--book">
                  <p className="tk-result-label">Book</p>
                  <p className="tk-result-book">{result.bookTitle}</p>
                </div>
              )}

              <div className="tk-result-meta">
                <p className="tk-result-label">Reference Number</p>
                <p className="tk-result-ack">{result.acknowledgementNumber}</p>
              </div>

              <div className="tk-result-meta">
                <p className="tk-result-label">Current Status</p>
                <p
                  className={`tk-result-status ${
                    result.isComplete ? "tk-result-status--done" : ""
                  }`}
                >
                  {result.currentStatus}
                </p>
              </div>
            </div>

            {result.isComplete && (
              <div className="tk-complete" role="status">
                <PackageCheck size={18} strokeWidth={2} />
                <span>Your order has shipped.</span>
              </div>
            )}

            <div className="tk-timeline-wrap">
              <p className="tk-timeline-title">Progress</p>
              <TrackTimeline timeline={result.timeline} />
            </div>

            <p className="tk-photo-note">
              Photos shown in the progress above are removed after 7 days.
            </p>

          </div>
        )}
      </div>
    </section>
  );
};

export default TrackEnquiry;
